import { useState } from "react";
import Dataset from "datasets/types/interfaces/dataset";
import { AppWindow, ChevronRight, FlaskConical, Grid2x2Plus, Plus, Trash2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import {
  MCP_APP_GROUP_LABELS,
  type McpAppTab,
} from "~/pages/manifold-nextgen/components/conversations/useMcpAppsPane";
import type { SessionEvalRun } from "~/pages/manifold-nextgen/components/conversations/useSessionEvalRuns";
import type { SessionIngest } from "~/pages/manifold-nextgen/components/conversations/useSessionIngests";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { useProjectEntityRemoval } from "~/pages/manifold-nextgen/hooks/useProjectEntityRemoval";
import { Agent } from "~/services/agents";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";

interface SessionContextTocProps {
  projectId: string;
  isOwner: boolean;
  attachedDatasets: Dataset[] | undefined;
  attachedAgents: Agent[] | undefined;
  onOpenDataset: (datasetId: string) => void;
  // Open app instances across all servers — grouped by server (e.g. all open
  // cohorts) into a single row per group.
  apps: McpAppTab[];
  onOpenAgent: (agentId: string) => void;
  onOpenApp: (serverId: string) => void;
  // Ingestions created during this session; the entry opens the full list.
  ingests: SessionIngest[];
  onOpenIngests: () => void;
  // Eval runs launched during this session; the entry opens the full list.
  evalRuns: SessionEvalRun[];
  onOpenEvalRuns: () => void;
}

export const SessionContextToc = ({
  projectId,
  isOwner,
  attachedDatasets,
  attachedAgents,
  onOpenDataset,
  apps,
  onOpenAgent,
  onOpenApp,
  ingests,
  onOpenIngests,
  evalRuns,
  onOpenEvalRuns,
}: SessionContextTocProps) => {
  const [datasetsOpen, setDatasetsOpen] = useState(true);
  const [agentsOpen, setAgentsOpen] = useState(true);
  const [appsOpen, setAppsOpen] = useState(true);
  const [addDatasetsOpen, setAddDatasetsOpen] = useState(false);
  const [addAgentsOpen, setAddAgentsOpen] = useState(false);

  const { handleRemoveDataset, handleRemoveAgent, isRemovingDataset, isRemovingAgent } =
    useProjectEntityRemoval(projectId, attachedDatasets, attachedAgents);

  const appServerIds = [...new Set(apps.map((tab) => tab.serverId))];

  return (
    <div className="flex flex-col gap-1 py-2">
      <TocSection
        label="Datasets"
        open={datasetsOpen}
        onToggle={() => setDatasetsOpen((prev) => !prev)}
        onAdd={isOwner ? () => setAddDatasetsOpen(true) : undefined}
        addLabel="Add dataset"
      >
        {(attachedDatasets ?? []).length === 0 ? (
          <TocEmptyRow label="No datasets attached." />
        ) : (
          (attachedDatasets ?? []).map((dataset) => (
            <TocRow
              key={dataset.id}
              onClick={() => onOpenDataset(dataset.id)}
              leading={<DatasetIcon dataset={dataset} size={18} />}
              label={dataset.title}
              onRemove={isOwner ? () => handleRemoveDataset(dataset.id) : undefined}
              removeLabel={`Remove ${dataset.title}`}
              isRemoving={isRemovingDataset}
            />
          ))
        )}
      </TocSection>

      <div className="my-1 h-px bg-border" />

      <TocSection
        label="Agents"
        open={agentsOpen}
        onToggle={() => setAgentsOpen((prev) => !prev)}
        onAdd={isOwner ? () => setAddAgentsOpen(true) : undefined}
        addLabel="Add agent"
      >
        {(attachedAgents ?? []).length === 0 ? (
          <TocEmptyRow label="No agents attached." />
        ) : (
          (attachedAgents ?? []).map((agent) => (
            <TocRow
              key={agent.id}
              onClick={() => onOpenAgent(agent.id)}
              leading={<AgentAvatar agent={agent} size={18} />}
              label={agent.display_name}
              onRemove={isOwner ? () => handleRemoveAgent(agent.id) : undefined}
              removeLabel={`Remove ${agent.display_name}`}
              isRemoving={isRemovingAgent}
            />
          ))
        )}
      </TocSection>

      <div className="my-1 h-px bg-border" />

      {/* Ingestions live inside the Apps section as a single entry (both are
          session-scoped runtime artifacts); opening it shows the full list. */}
      <TocSection label="Apps" open={appsOpen} onToggle={() => setAppsOpen((prev) => !prev)}>
        {appServerIds.length === 0 && ingests.length === 0 && evalRuns.length === 0 ? (
          <TocEmptyRow label="No apps open." />
        ) : (
          <>
            {appServerIds.map((serverId) => (
              <TocRow
                key={serverId}
                onClick={() => onOpenApp(serverId)}
                leading={<AppWindow className="size-4.5 shrink-0 text-muted-foreground" />}
                label={MCP_APP_GROUP_LABELS[serverId] ?? serverId}
              />
            ))}
            {ingests.length > 0 && (
              <TocRow
                onClick={onOpenIngests}
                leading={<Grid2x2Plus className="size-4.5 shrink-0 text-muted-foreground" />}
                label="Ingestions"
              />
            )}
            {evalRuns.length > 0 && (
              <TocRow
                onClick={onOpenEvalRuns}
                leading={<FlaskConical className="size-4.5 shrink-0 text-muted-foreground" />}
                label="Eval runs"
              />
            )}
          </>
        )}
      </TocSection>

      <ProjectDatasetsDialog
        projectId={projectId}
        open={addDatasetsOpen}
        onOpenChange={setAddDatasetsOpen}
        attachedIds={(attachedDatasets ?? []).map((d) => d.id)}
      />
      <ProjectAgentsDialog
        projectId={projectId}
        open={addAgentsOpen}
        onOpenChange={setAddAgentsOpen}
        attached={attachedAgents ?? []}
      />
    </div>
  );
};

interface TocSectionProps {
  label: string;
  open: boolean;
  onToggle: () => void;
  onAdd?: () => void;
  addLabel?: string;
  children: React.ReactNode;
}

const TocSection = ({ label, open, onToggle, onAdd, addLabel, children }: TocSectionProps) => (
  <div className="px-2">
    <div className="group/section flex items-center gap-1 rounded-md px-2 py-1">
      <button
        type="button"
        onClick={onToggle}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
      >
        <ChevronRight className={cn("size-3 shrink-0 transition-transform", open && "rotate-90")} />
        {label}
      </button>
      {onAdd && (
        <Button
          variant="ghost"
          size="icon"
          className="size-5.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/section:opacity-100"
          onClick={onAdd}
          aria-label={addLabel}
          title={addLabel}
        >
          <Plus className="size-3.5" />
        </Button>
      )}
    </div>
    {open && <div className="flex flex-col gap-0.5">{children}</div>}
  </div>
);

interface TocRowProps {
  onClick: () => void;
  leading: React.ReactNode;
  label: string;
  onRemove?: () => void;
  removeLabel?: string;
  isRemoving?: boolean;
}

const TocRow = ({ onClick, leading, label, onRemove, removeLabel, isRemoving }: TocRowProps) => {
  return (
    <div className="group flex items-center gap-1 rounded-md px-2 py-1.5 hover:bg-accent/50">
      <button
        type="button"
        onClick={onClick}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left"
      >
        {leading}
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label}</span>
      </button>
      {onRemove && (
        <Button
          variant="ghost"
          size="icon"
          className="size-6 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
          onClick={onRemove}
          disabled={isRemoving}
          aria-label={removeLabel}
        >
          <Trash2 className="size-3.5" />
        </Button>
      )}
    </div>
  );
};

const TocEmptyRow = ({ label }: { label: string }) => (
  <p className="px-2 py-1 text-xs italic text-muted-foreground/80">{label}</p>
);
